import { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { PushContext } from '@hooks/usePush/PushContext.tsx';
import { usePushEvent } from '@hooks/usePush/usePushEvent.ts';
import { useAuthReady } from '@hooks/useAuthReady';

const POLL_FALLBACK_MS = 60000;

/**
 * Company-wide jobs-center menu badge counts: red = jobs in ERROR, yellow = DONE with failed
 * items. Push-first: refreshes on `job.finished` / `job.archived` hints from the company-jobs
 * channel (debounced — a burst of finishes triggers one refetch); while push is down, a slow
 * 60s poll keeps the badge honest. `enabled` gates everything — pass the jobs-menu visibility
 * so users without jobs permissions never hit the endpoint.
 */
export const useJobBadgeCounts = (enabled: boolean) => {
  const { getJobBadgeCounts } = useContext(ApiContext);
  const { connectionState } = useContext(PushContext);
  const authReady = useAuthReady();
  const [errorCount, setErrorCount] = useState(0);
  const [doneWithErrorCount, setDoneWithErrorCount] = useState(0);
  const debounceRef = useRef<number | null>(null);

  const active = enabled && authReady;
  const activeRef = useRef(active);
  activeRef.current = active;

  const refetch = () => {
    if (!activeRef.current) return;
    getJobBadgeCounts(data => {
      setErrorCount(data?.errorCount ?? 0);
      setDoneWithErrorCount(data?.doneWithErrorCount ?? 0);
    });
  };

  const refetchDebounced = () => {
    if (debounceRef.current != null) return;
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      refetch();
    }, 500);
  };

  useEffect(() => {
    if (!active) return;
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(
    () => () => {
      if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
    },
    [],
  );

  // Counts change on terminal transitions, archivals — and on retry, which re-queues an ERROR /
  // DONE-with-failed job and hints it as a PENDING job.progress. Plain RUNNING item ticks are
  // ignored, so a long job doesn't hammer the endpoint.
  usePushEvent(
    e => {
      if (e.type === 'job.progress' && e.status !== 'PENDING') return;
      refetchDebounced();
    },
    { types: ['job.progress', 'job.finished', 'job.archived'], onReconnect: () => refetch() },
  );

  useEffect(() => {
    if (!active || connectionState === 'connected') return;
    const id = window.setInterval(refetch, POLL_FALLBACK_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, connectionState]);

  return { errorCount, doneWithErrorCount };
};
